import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { CheckCircle2 } from "lucide-react";
import { forwardRef, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const apiUrl = import.meta.env.VITE_CLOUDFLARE_API_URL || "http://localhost:8788";

interface AppSettings {
  company_name?: string;
  company_address_line1?: string;
  company_address_line2?: string;
  company_phone?: string;
  company_website?: string;
  company_abn?: string;
  quote_intro_text?: string;
  quote_terms_text?: string;
  logo_url?: string;
}

interface ChecklistResponse {
  [field: string]: any;
}

interface ChecklistData {
  checklist_name: string;
  responses: ChecklistResponse;
  estimated_cost?: number;
}

interface ServiceData {
  service_name: string;
  instance_name?: string;
  checklists: ChecklistData[];
}

interface QuoteData {
  quote_name: string;
  quote_number: string;
  status: string;
}

interface SummaryData {
  estimated_total: number;
  total_services: number;
  total_checklists: number;
}

interface ProjectData {
  customer_name?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
}

export interface ScopeOfWorkData {
  quote: QuoteData;
  services: ServiceData[];
  summary: SummaryData;
  project?: ProjectData;
}

interface ScopeOfWorkViewProps {
  data: ScopeOfWorkData;
  showPrintHeader?: boolean;
}

/**
 * Formats a checklist response value into a human-readable string
 */
function formatResponseValue(value: any): string {
  if (value === null || value === undefined) {
    return "";
  }

  // Handle response entry format: { value: X, price?: Y }
  if (typeof value === "object" && value !== null) {
    if ("value" in value) {
      const displayValue = value.value;
      const price = value.price;

      if (displayValue === null || displayValue === undefined || displayValue === "") {
        return "";
      }

      // Format the value
      let formatted = String(displayValue);

      // Add price if available
      if (price !== null && price !== undefined && price !== 0) {
        formatted += ` (@ $${Number(price).toFixed(2)})`;
      }

      return formatted;
    }

    // Handle other object types (arrays, plain objects)
    if (Array.isArray(value)) {
      return value.map(formatResponseValue).filter(Boolean).join(", ");
    }

    // Plain objects - convert to readable format
    return Object.entries(value)
      .map(([k, v]) => `${k}: ${formatResponseValue(v)}`)
      .filter(Boolean)
      .join(", ");
  }

  // Primitives
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  return String(value);
}

/**
 * Extracts meaningful scope items from checklist responses
 */
function extractScopeItems(responses: ChecklistResponse): string[] {
  const items: string[] = [];

  for (const [field, value] of Object.entries(responses)) {
    const formatted = formatResponseValue(value);

    if (!formatted) {
      continue;
    }

    // Clean up field name for display (convert camelCase/snake_case to Title Case)
    const fieldName = field
      .replace(/_/g, " ")
      .replace(/([A-Z])/g, " $1")
      .trim()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");

    items.push(`${fieldName}: ${formatted}`);
  }

  return items;
}

export const ScopeOfWorkView = forwardRef<HTMLDivElement, ScopeOfWorkViewProps>(
  ({ data, showPrintHeader = true }, ref) => {
    const { quote, services, summary, project } = data;
    const today = new Date();

    // Fetch company settings
    const { data: settingsData } = useQuery({
      queryKey: ["app-settings"],
      queryFn: async () => {
        const response = await fetch(`${apiUrl}/api/v1/settings`);
        if (!response.ok) throw new Error("Failed to fetch settings");
        const data = await response.json();
        return data.data as AppSettings;
      },
    });

    // Flatten all scope items from all services and checklists
    const allScopeItems: Array<{ serviceInstanceName: string; items: string[] }> = [];

    for (const service of services) {
      const serviceDisplayName = service.instance_name || service.service_name;

      for (const checklist of service.checklists) {
        const items = extractScopeItems(checklist.responses);

        if (items.length > 0) {
          allScopeItems.push({
            serviceInstanceName: `${serviceDisplayName} - ${checklist.checklist_name}`,
            items,
          });
        }
      }
    }

    return (
      <div ref={ref} className="min-h-screen bg-white print:bg-white">
        {/* Print Header */}
        {showPrintHeader && (
          <div className="border-b-2 border-gray-300 bg-white print:block">
            <div className="container mx-auto px-8 py-6">
              {/* 50/50 Header: Company Info Left | Logo Right */}
              <div className="grid grid-cols-2 gap-8 mb-6">
                {/* Left: Company Information */}
                <div className="space-y-1">
                  <h2 className="text-2xl font-bold text-gray-900 uppercase">
                    {settingsData?.company_name || "RENO WARRIORS PTY LTD"}
                  </h2>
                  <p className="text-sm text-gray-700">
                    {settingsData?.company_address_line1 || "Po Box 25"}
                  </p>
                  <p className="text-sm text-gray-700">
                    {settingsData?.company_address_line2 || "Patterson Lakes Vic 3197"}
                  </p>
                  <p className="text-sm text-gray-700">
                    {settingsData?.company_phone || "Ph: 1300 788 815"}
                  </p>
                  <p className="text-sm text-gray-700">
                    {settingsData?.company_website || "www.renowarriors.com.au"}
                  </p>
                  <p className="text-sm text-gray-700 font-medium">
                    {settingsData?.company_abn || "ABN: 70 618 035 377"}
                  </p>
                </div>

                {/* Right: Logo */}
                <div className="flex items-start justify-end">
                  {settingsData?.logo_url && (
                    <div className="w-48 h-32 flex items-center justify-center">
                      <img
                        src={`${apiUrl}${settingsData.logo_url}`}
                        alt="Company Logo"
                        crossOrigin="anonymous"
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* 33/33/33 Row: Quote Date | Performed By | Performed For */}
              <div className="grid grid-cols-3 gap-6 mb-6 border-t border-gray-200 pt-6">
                {/* Left: Quote Information */}
                <div>
                  <h3 className="text-xs font-semibold text-gray-600 uppercase mb-2">Quotation</h3>
                  <div className="space-y-1 text-sm">
                    <p className="font-bold">{quote.quote_number}</p>
                    <p className="text-gray-700">{format(today, "dd MMMM yyyy")}</p>
                  </div>
                </div>

                {/* Middle: Services Performed By */}
                <div>
                  <h3 className="text-xs font-semibold text-gray-600 uppercase mb-2">
                    Services Performed By
                  </h3>
                  <div className="space-y-0.5 text-sm text-gray-700">
                    <p className="font-medium">
                      {settingsData?.company_name || "RENO WARRIORS PTY LTD"}
                    </p>
                    <p>{settingsData?.company_address_line1 || "Po Box 25"}</p>
                    <p>{settingsData?.company_address_line2 || "Patterson Lakes Vic 3197"}</p>
                  </div>
                </div>

                {/* Right: Services Performed For (Customer) */}
                <div>
                  <h3 className="text-xs font-semibold text-gray-600 uppercase mb-2">
                    Services Performed For
                  </h3>
                  <div className="space-y-0.5 text-sm text-gray-700">
                    <p className="font-medium">{project?.customer_name || "Customer Name"}</p>
                    {project?.address && (
                      <p>
                        {project.address}
                        {project.city ? `, ${project.city}` : ""}
                      </p>
                    )}
                    {project?.phone && <p>{project.phone}</p>}
                    {project?.email && <p>{project.email}</p>}
                  </div>
                </div>
              </div>

              {/* 100% Width: Introduction Text */}
              {settingsData?.quote_intro_text && (
                <div className="border-t border-gray-200 pt-6">
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {settingsData.quote_intro_text}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="container mx-auto px-6 py-8">
          {/* Scope of Work Section */}
          <Card className="border-2 border-gray-200 shadow-sm mb-6">
            <CardHeader className="bg-gray-50 border-b">
              <CardTitle className="text-2xl font-bold text-gray-900">Scope of Work</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              {allScopeItems.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No scope items defined yet.</p>
                  <p className="text-sm mt-2">Complete the checklists to generate scope of work.</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {allScopeItems.map((serviceGroup, groupIndex) => (
                    <div key={groupIndex} className="space-y-3">
                      {/* Service Instance Header */}
                      <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-800">
                          {groupIndex + 1}. {serviceGroup.serviceInstanceName.toUpperCase()}
                        </h3>
                      </div>

                      {/* Scope Items List */}
                      <div className="ml-4 space-y-2">
                        {serviceGroup.items.map((item, itemIndex) => {
                          // Extract letter for sub-items (a, b, c, etc.)
                          const letter = String.fromCharCode(97 + itemIndex); // 97 is 'a'

                          return (
                            <div key={itemIndex} className="flex items-start gap-3 group">
                              <span className="text-sm font-medium text-gray-600 min-w-[24px]">
                                {letter})
                              </span>
                              <div className="flex-1 flex items-start gap-2">
                                <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                <p className="text-gray-700 leading-relaxed">{item}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quote Summary */}
          <Card className="border-2 border-gray-200 shadow-sm">
            <CardHeader className="bg-gray-50 border-b">
              <CardTitle className="text-xl font-bold text-gray-900">Quote Summary</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-3">
                <div className="flex justify-between text-gray-700">
                  <span>Subtotal (excl. GST):</span>
                  <span className="font-medium">
                    $
                    {(summary.estimated_total / 1.1).toLocaleString("en-AU", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
                <div className="flex justify-between text-gray-700">
                  <span>GST (10%):</span>
                  <span className="font-medium">
                    $
                    {((summary.estimated_total * 0.1) / 1.1).toLocaleString("en-AU", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
                <div className="flex justify-between font-bold text-xl border-t-2 pt-3 text-gray-900">
                  <span>Total (incl. GST):</span>
                  <span className="text-green-600">
                    $
                    {summary.estimated_total.toLocaleString("en-AU", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
              </div>

              {/* Additional Info */}
              <div className="mt-6 pt-6 border-t border-gray-200 grid grid-cols-2 gap-4 text-sm text-gray-600">
                <div>
                  <span className="font-medium">Total Services:</span> {summary.total_services}
                </div>
                <div>
                  <span className="font-medium">Total Checklists:</span> {summary.total_checklists}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Terms & Conditions */}
          {settingsData?.quote_terms_text && (
            <Card className="border-2 border-gray-200 shadow-sm mt-6">
              <CardHeader className="bg-gray-50 border-b">
                <CardTitle className="text-xl font-bold text-gray-900">
                  Terms & Conditions
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {settingsData.quote_terms_text
                    .replace(/{quote_number}/g, quote.quote_number)
                    .replace(/{quote_date}/g, format(today, "dd MMMM yyyy"))}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Footer Note with Page Number */}
          <div className="mt-8 text-sm text-gray-500 text-center print:block print:fixed print:bottom-4 print:left-0 print:right-0">
            <p>This quote is valid for 30 days from the date of issue.</p>
            <p className="mt-1">Quote generated on {format(today, "MMMM dd, yyyy 'at' h:mm a")}</p>
            <p className="mt-2 print:block hidden">
              Page <span className="page-number" />
            </p>
          </div>
        </div>

        {/* Print-specific page counter styles */}
        <style
          dangerouslySetInnerHTML={{
            __html: `
          @media print {
            @page {
              margin: 0.5cm;
              size: A4 portrait;
            }

            /* Remove all screen margins and padding */
            * {
              margin: 0 !important;
              padding: 0 !important;
            }

            body {
              margin: 0 !important;
              padding: 0 !important;
              counter-reset: page;
            }

            /* Reset main container */
            .min-h-screen {
              min-height: auto !important;
              margin: 0 !important;
              padding: 0 !important;
            }

            /* Remove ALL container margins and padding */
            .container {
              max-width: 100% !important;
              margin: 0 !important;
              padding: 0.3cm !important;
              width: 100% !important;
            }

            /* Remove card padding */
            .px-8, .px-6 {
              padding-left: 0.3cm !important;
              padding-right: 0.3cm !important;
            }

            .py-8, .py-6 {
              padding-top: 0.3cm !important;
              padding-bottom: 0.3cm !important;
            }

            /* Card spacing */
            .pt-6 {
              padding-top: 0.3cm !important;
            }

            .pb-6 {
              padding-bottom: 0.3cm !important;
            }

            /* Margin resets */
            .mb-6 {
              margin-bottom: 0.2cm !important;
            }

            .mt-6, .mt-8 {
              margin-top: 0.2cm !important;
            }

            /* Remove card shadows and borders */
            .shadow-sm {
              box-shadow: none !important;
            }

            .border-2 {
              border-width: 1px !important;
            }

            /* Grid spacing */
            .gap-8 {
              gap: 0.3cm !important;
            }

            .gap-6 {
              gap: 0.3cm !important;
            }

            /* Space between items */
            .space-y-1 > * + * {
              margin-top: 0.1cm !important;
            }

            .space-y-3 > * + * {
              margin-top: 0.15cm !important;
            }

            .space-y-6 > * + * {
              margin-top: 0.3cm !important;
            }

            /* Page counter */
            .page-number::after {
              counter-increment: page;
              content: counter(page);
            }

            /* Smart page breaks */
            .space-y-6 > div {
              page-break-inside: avoid;
              break-inside: avoid;
            }

            /* Prevent orphaned headers */
            h1, h2, h3, h4 {
              page-break-after: avoid;
              break-after: avoid;
            }

            /* Keep scope items together */
            .space-y-3 {
              page-break-inside: avoid;
              break-inside: avoid;
            }

            /* Allow cards to break if needed */
            .border-gray-200 {
              page-break-inside: auto;
              break-inside: auto;
            }

            /* Footer positioning */
            .print\\:fixed {
              position: relative !important;
            }

            .print\\:bottom-4 {
              bottom: auto !important;
            }
          }
        `,
          }}
        />
      </div>
    );
  }
);

ScopeOfWorkView.displayName = "ScopeOfWorkView";
